import List from "components/list";
import { useParams } from "react-router-dom";
import { usePermissions } from "contexts/permission";
import { EmployeeCell } from "components/cell/employee/cell";
import { getEmployeeFields } from "components/user/config";
import { Button } from "ui/index";
import { Icon } from "components/index";
import { EDIT } from "icons/src";
import EmployeeInput from "components/employee/input";
import { useMutation } from "@tanstack/react-query";
import { updateItemInCollection } from "directus/src/operations";
import { invalidateAllQueries } from "utils/utils";
import { eventBus } from "utils/eventBus";
import { toast } from "sonner";
import { UserCell } from "components/user/cell"
import { UserFieldInput } from "permissions/components/user-field-input";
import { ROUTE } from "@/lib/utils";

export default function ReviewerList() {
  const { employeeId } = useParams();
  const permissions = usePermissions();
  const canEditReviewer = permissions.hasFieldAccess("jobs_employees", "update", "reviewer");

  const reviewerFilter = employeeId ? { _eq: employeeId } : { user: { _eq: "$CURRENT_USER" } };

  const storageKey = employeeId ? `profile-reviewer-jobs-employees-${employeeId}` : "profile-reviewer-jobs-employees";

  const updateReviewerMutation = useMutation({
    mutationFn: async ({ id, reviewerId }) => {
      await updateItemInCollection("jobs_employees", id, { reviewer: reviewerId });
    },
    onSuccess: () => {
      invalidateAllQueries();
      toast.success("Reviewer Updated Successfully");
      eventBus.emit("refresh:list", `jobs_employees${storageKey}`);
    },
    onError: (error) => {
      toast.error(error.message)
    }
  });

  const fields = [
    "id",
    "job.id",
    "job.name",
    "job.project.name",
    getEmployeeFields("employee"),
    getEmployeeFields("reviewer"),
  ];

  const columns = [
    {
      id: "project",
      header: "Project",
      accessorKey: "job.project.name",
    },
    {
      id: "job",
      header: "Job",
      accessorKey: "job.name",
    },
    {
      id: "employee",
      header: "Employee",
      accessorKey: "employee",
      cell: UserCell,
    },
    {
      id: "reviewer",
      header: "Reviewer",
      hidden: !canEditReviewer,
      href: false,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <UserFieldInput
            targetItem={item.id}
            field="reviewer"
            collection="jobs_employees"
            showWhenDisabled={false}
          >
            <EmployeeInput
              set="all"
              value={item.reviewer}
              singleSelect={true}
              footer={({ setOpen, selectedEmployees }) => (
                <Button
                  onClick={async () => {
                    const selectedEmployee = selectedEmployees?.[0];
                    if (selectedEmployee) {
                      await updateReviewerMutation.mutateAsync({
                        id: item.id,
                        reviewerId: selectedEmployee.id,
                      });
                    }
                    setOpen(false);
                  }}
                >
                  Assign
                </Button>
              )}
            >
              <Button variant="ghost">
                <Icon icon={EDIT} />
              </Button>
            </EmployeeInput>
          </UserFieldInput>
        );
      }
    }
  ];

  const filters = {
    reviewer: reviewerFilter,
    job: {
      status: { _nin: ["archived", "closed", "cancelled"] }
    }
  };

  return (
    <List
      title="Reviewer"
      storageKey={storageKey}
      endPoint="jobs_employees"
      filters={filters}
      columns={columns.filter(col => !col.hidden)}
      meta={{ fields }}
      href={(item) => ROUTE.JOB_EMPLOYEE(item.job.id, item.id)}
    />
  );
}
